import type { ReactElement } from 'react';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import { ImageSlot } from '../../components/ImageSlot';
import { artProductId, productLabel, tacticFor } from '../../lib/plan';
import { useBindings } from '../../state/BindingsContext';
import { useContentLibrary } from '../../state/ContentLibraryContext';
import { isKitAsset, productDeviceAsset } from './assets';
import { ChannelHero, channelHeroModel, type ChannelHeroKind } from './ChannelHero';
import { Frame, focalPointStyle } from './primitives';
import { SearchDeviceVisual, searchHeroModel } from './SearchDeviceVisual';

/**
 * Slide 09 (media-pair-contract.md): `paid_search` on v3 gets the
 * `SearchDeviceVisual` hero (a laptop + phone search mockup) instead of the
 * shared kit's device art. Every other productId keeps the kit/photo hero
 * below, unchanged.
 */
const SEARCH_HERO_PRODUCT = 'paid_search';

/**
 * media-pairs-all-contract.md "Cover heroes: `ChannelHero`" — every other
 * catalog product gets an inline device-mockup hero built for its ad format
 * instead of the shared kit's device/photo art. A productId not in this map
 * (none exist in the current catalog, but a future one might) falls back to
 * the pre-existing kit/photo hero below, unchanged.
 */
const CHANNEL_HERO_KINDS: Record<string, ChannelHeroKind> = {
  social: 'social',
  ctv: 'ctv',
  targeted_display: 'targeted_display',
  online_video: 'online_video',
  streaming_audio: 'streaming_audio',
  email: 'email',
  mobile_location: 'mobile_location',
};

/**
 * v3-only hero resolver (audit `~/Downloads/proposal-slide-audit.md` Slides
 * 9/12/16 "Asset direction" + the follow-up divider-asset audit): several of
 * the shared kit's per-product fallbacks (`../momentum/assets`
 * `PRODUCT_DEVICE`) bake unrelated third-party "BidMatrix" ad creative into
 * the device screen - fine for momentum_v1/v2 (byte-identical, untouched),
 * wrong for v3, whose dividers must never show another company's brand.
 * Confirmed by opening every device PNG under `web/public/assets/momentum/
 * devices/`:
 *   - social (meta-phone.png), targeted_display / mobile_location
 *     (display-laptop-phone.png), email (email-phone.png) and online_video
 *     (video-laptop.png) all render a "BidMatrix" logo/wordmark on screen.
 *   - paid_search (sem-laptop.png) and ctv (ctv-tv.png) show generic,
 *     brand-free dashboard/streaming-ad mockups - kept as-is.
 *   - streaming_audio already falls back to pulse-laptop.png (no dedicated
 *     kit asset), which is itself the agency's own "Pulse" product mock -
 *     the safe generic fallback every BidMatrix-branded product below
 *     redirects to.
 * Until the agent sets a campaign-relevant photo, the affected productIds
 * fall back to the generic pulse-laptop device art instead of their
 * BidMatrix-branded kit asset.
 */
const V3_BRANDED_ASSET_PRODUCTS = new Set(['social', 'targeted_display', 'mobile_location', 'email', 'online_video']);

function v3HeroSrc(imageUrl: string | null | undefined, productId: string | undefined): string {
  if (imageUrl) return imageUrl;
  if (productId && V3_BRANDED_ASSET_PRODUCTS.has(productId)) return productDeviceAsset(undefined);
  return productDeviceAsset(productId);
}

/**
 * product_divider (`docs/momentum-v3-slides.md`; audit Slides 9/12/16 +
 * "Targeted Display divider consistency note"): one clean chapter break per
 * tactic - channel name, an optional one-line human role, one hero asset.
 * No product numbering, no funnel-stage label, no "CLIENT PLAN" breadcrumb,
 * no pinstripes. The hero is kit device art `contain`-fit inside a bounded,
 * safe-margined region (audit: "do not let meaningful parts of the asset
 * get clipped by the right or bottom edge") - a rep/agent-set
 * `content.imageUrl` photo instead renders `cover`-fit, unbounded, exactly
 * as before.
 */
export function ProductDividerPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const resolve = useBindings();
  const { productName } = useContentLibrary();
  const productId = section.productId;
  const label = productId ? (productName(productId) ?? productLabel(proposal, productId)) : section.title;
  const imageUrl = section.content.imageUrl;
  const artId = productId ? artProductId(proposal, productId) : undefined;
  const isSearchHero = artId === SEARCH_HERO_PRODUCT;
  const channelHeroKind = artId ? CHANNEL_HERO_KINDS[artId] : undefined;
  const heroSrc = isSearchHero || channelHeroKind ? null : v3HeroSrc(imageUrl, artId);
  const isDeviceArt = heroSrc ? isKitAsset(heroSrc) : false;
  const tactic = productId ? tacticFor(proposal, productId) : null;
  const roleLine = section.content.headline || tactic?.role || '';
  /** media-pairs-all-contract.md: `ChannelHero` reuses the exact hero-box
   * geometry `SearchDeviceVisual` established (47% title column / 53% hero,
   * 76px title, vertical centring) via the shared `-device` modifier
   * classes (`momentum-v3-heroes.css`) — siblings of `SearchDeviceVisual`'s
   * own `-search` ones in `momentum-v3-mediapair.css`, never touching them. */
  const inlineHeroModifier = isSearchHero ? '-search' : channelHeroKind ? '-device' : '';

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-divider-root"
      hideTitle
    >
      <Frame surface="dark" proposal={proposal} section={section}>
        <div className="mv3-divider-grid">
          <div className={`mv3-divider-left${inlineHeroModifier ? ` mv3-divider-left${inlineHeroModifier}` : ''}`}>
            <h1 className={`mv3-divider-name${inlineHeroModifier ? ` mv3-divider-name${inlineHeroModifier}` : ''}`}>{label}</h1>
            <div className="mv3-divider-descriptor">
              <EditableText
                value={roleLine}
                field="headline"
                placeholder="Add a one-line role (optional)"
                readOnly={readOnly}
                onSave={(headline) => actions.saveContent(section, { headline })}
              />
            </div>
          </div>
          <div className={`mv3-divider-hero${inlineHeroModifier ? ` mv3-divider-hero${inlineHeroModifier}` : ''}`}>
            {isSearchHero ? (
              <SearchDeviceVisual model={searchHeroModel(proposal, section, resolve)} className="mv3-divider-searchhero" />
            ) : channelHeroKind ? (
              <ChannelHero
                kind={channelHeroKind}
                model={channelHeroModel(proposal, section, resolve)}
                className="mv3-divider-channelhero"
              />
            ) : (
              <img
                src={heroSrc ?? undefined}
                alt={label}
                className={`mv3-divider-image${isDeviceArt ? ' mv3-divider-image-device' : ''}`}
                style={focalPointStyle(section)}
              />
            )}
            {!readOnly ? (
              <ImageSlot
                url={imageUrl}
                alt={label}
                readOnly={readOnly}
                onSave={(next) => actions.saveContent(section, { imageUrl: next })}
                className="mv3-divider-image-affordance"
                hideImage
                proposalId={proposal.proposalId}
                defaultQuery={section.title}
              />
            ) : null}
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}
